import React, { useEffect } from "react";
import { Box, Grid, Typography, Stack, Button } from "@mui/material";
import useBreadcrumb from "../../hooks/useBreadcrumb";
import styles from './index.module.css'
import SustainabilityImg from "../../assets/images/sustain-cover-img.png";
import Img1 from "../../assets/images/Img 1.png";
import Img2 from "../../assets/images/Img 2.png";
import Img3 from "../../assets/images/Img 3.png";
import Img4 from "../../assets/images/Img 4.png";
import Img5 from "../../assets/images/Img 5.png";
import CheckBoxIcon from "../../assets/icons/Bck/Group 18236.png";


function Sustainability() {
    const { setBreadcrumb, clearBreadcrumb } = useBreadcrumb();
    const sections = [
        {
            id: 1,
            title: "Smarter Use of Medications & Medical Equipment",
            description:
                "We work closely with patients to ensure medications and durable medical equipment (DME) are used safely, effectively, and appropriately.",
            points: [
                "Medication reviews to reduce duplication and overuse",
                "Refill synchronization and adherence support",
                "Patient education on correct medication and DME use",
                "Guidance on safe medication disposal",
            ],
            why: "Fewer unused medications and supplies mean better outcomes and less waste.",
            image: Img1,
        },
        {
            id: 2,
            title: "Home-Based & Preventive Care",
            description:
                "Our Remote Patient Monitoring (RPM) and Chronic Care Management (CCM) programs help patients manage chronic conditions from home.",
            points: [
                "Continuous monitoring for conditions such as diabetes, hypertension, and heart disease",
                "Proactive follow-up to address issues early",
                "Focus on stability, prevention, and long-term health",
            ],
            why:
                "Preventing complications reduces hospital visits and improves quality of life.",
            image: Img2,
        },
        {
            id: 3,
            title: "Lean, Low-Waste Pharmacy Operations",
            description:
                "As a small, independent pharmacy, we focus on efficiency and responsibility in our daily operations.",
            points: [
                "Paper-light workflows and digital communication",
                "Careful inventory management to reduce expired medications and supplies",
                "Energy-conscious practices throughout our pharmacy",
            ],
            why:
                "Efficient operations reduce costs, waste, and unnecessary resource use.",
            image: Img3,
        },
        {
            id: 4,
            title: "Digital & Connected Care",
            description:
                "We use technology to improve access to care while minimizing unnecessary in-person visits.",
            points: [
                "Telephonic and virtual check-ins for eligible patients",
                "Digital reminders and patient education tools",
                "Streamlined documentation to improve care coordination",
            ],
            why:
                "Connected care improves access while reducing time and travel burdens.",
            image: Img4,
        },
        {
            id: 5,
            title: "People & Community First",
            description:
                "Sustainability starts with people—our patients, our staff, and our community.",
            points: [
                "Staff trained in patient education and responsible dispensing",
                "Empowering patients to actively manage their health",
                "Strong collaboration with local providers and caregivers",
            ],
            why:
                "Healthy communities are resilient communities.",
            image: Img5,
        },
    ];
    const [activeImage, setActiveImage] = React.useState(sections[0].image);



    useEffect(() => {
        setBreadcrumb({
            title: "Sustainability",
            links: [
                { title: "Home", url: "/" },
                { title: "Sustainability", url: "/sustainability" },
            ],
        });
        return clearBreadcrumb;
    }, []);

    return (
        <Box sx={{ pt: 4 }}>
            <Box className={styles.content}>
                {/* Page Title */}
                <Typography
                    fontSize={"35px"}
                    fontWeight={700}
                    textAlign="center"
                    mb={6}
                >
                    Our Commitment to Sustainable Healthcare
                </Typography>

                {/* Main Content */}
                <Grid container spacing={6} sx={{ px: { xs: 2, md: 4 } }} alignItems="flex-start">
                    {/* Left Content */}
                    <Grid item xs={12} md={7}>
                        <Stack spacing={3} pt={8}>
                            <Typography fontWeight={700} fontSize={"25px"}>
                                Sustainability at SavCare Pharmacy
                            </Typography>

                            <Typography lineHeight={"35px"} fontSize={"16px"} >
                                At SavCare Pharmacy, sustainability means delivering better healthcare with less waste—helping our patients stay healthier at home while responsibly using medicines, medical equipment, and resources.
                                <br />As a community-based pharmacy and healthcare services provider in Foxboro, Massachusetts, we believe the most sustainable healthcare is preventive, local, and connected.
                            </Typography>

                            <Typography fontSize={"25px"} fontWeight={700} >
                                What Sustainability Means to Us
                            </Typography>

                            <Typography fontSize={"16px"} >
                                Sustainability is not just about the environment. In healthcare, it means:
                            </Typography>

                            <Stack spacing={1.2}>
                                {[
                                    "Preventing disease progression rather than reacting to emergencies",
                                    "Using medications and medical equipment wisely",
                                    "Reducing unnecessary waste and inefficiencies",
                                    "Improving long-term health outcomes for our patients",
                                    "Ensuring our pharmacy can continue serving the community for years to come",
                                ].map((item) => (
                                    <Stack
                                        key={item}
                                        direction="row"
                                        spacing={1.5}
                                        alignItems="center"
                                    >
                                        <Box
                                            component="img"
                                            src={CheckBoxIcon}
                                            alt="Checked"
                                            sx={{
                                                width: 20,
                                                height: 20,
                                                flexShrink: 0,
                                            }}
                                        />
                                        <Typography
                                            fontSize="16px"
                                            lineHeight="30px"
                                        >
                                            {item}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Stack>
                    </Grid>

                    {/* Right Content */}
                    <Grid item xs={12} md={5}>
                        <Box
                            sx={{
                                borderRadius: 2,
                                overflow: "hidden",
                                boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                            }}
                        >
                            <img
                                src={SustainabilityImg}
                                alt="Sustainable healthcare"
                                style={{ width: "100%", display: "block" }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            <Box
                sx={{
                    mx: "auto",
                    mt: 6,
                    overflow: "hidden",
                    backgroundColor: "#F2F6F7",
                }}
            >
                <Box className={styles.content}>
                    {/* STICKY HEADER */}
                    <Box
                        sx={{
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            py: 2,
                            //   borderBottom: "1px solid #e5e7e6",
                        }}
                    >
                        <Typography
                            textAlign="center"
                            fontSize={35}
                            fontWeight={700}
                        >
                            How We Practice Sustainable Healthcare
                        </Typography>
                    </Box>
                    {/* SCROLLABLE CONTENT */}
                    <Box sx={{ display: { xs: "none", md: "block" } }}>
                        <Grid container spacing={5}>
                            {/* LEFT – STICKY IMAGE */}
                            <Grid item md={6}>
                                <Box
                                    sx={{
                                        position: "sticky",
                                        top: 120,
                                        height: 520,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: "100%",
                                            maxWidth: 420,
                                            height: 420,
                                            borderRadius: 3,
                                            overflow: "hidden",
                                        }}
                                    >
                                        <img
                                            src={activeImage}
                                            alt="Sustainability"
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Grid>

                            {/* RIGHT – SCROLLABLE CONTENT */}
                            <Grid item md={6}>
                                <Box sx={{ height: 520, overflowY: "auto", pr: 2, scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
                                    {sections.map((section) => (
                                        <Box key={section.id} sx={{ py: 12 }} onMouseEnter={() => setActiveImage(section.image)}>
                                            {/* Section Title */}
                                            <Typography fontSize={25} fontWeight={700}>
                                                {section.title}
                                            </Typography>
                                            <Typography mt={2}>{section.description}</Typography>
                                            <Stack spacing={1.5} mt={2} >
                                                {section.points.map((p) => (
                                                    <Stack direction="row" spacing={1.5} alignItems="center" key={p}>
                                                        <Box
                                                            component="img"
                                                            src={CheckBoxIcon}
                                                            alt="Checked"
                                                            sx={{ width: 20, height: 20, flexShrink: 0 }}
                                                        />
                                                        <Typography fontSize={16} >
                                                            {p}
                                                        </Typography>
                                                    </Stack>
                                                ))}
                                            </Stack>

                                            {/* Why it matters */}
                                            <Typography mt={2}>
                                                <strong>Why it matters:</strong> {section.why}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>

                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            </Box>

            {/* XS & SM SCREENS */}
            <Box sx={{ display: { xs: "block", md: "none" } }}>
                {sections.map((section) => (
                    <Box key={section.id} sx={{ px: 2, py: 4 }}>
                        <Typography fontSize={22} fontWeight={700}>
                            {section.title}
                        </Typography>
                        <Typography mt={1.5} fontSize={16}>
                            {section.description}
                        </Typography>
                        <Stack spacing={1.2} mt={2}>
                            {section.points.map((p) => (
                                <Stack direction="row" spacing={1} alignItems="center" key={p}>
                                    <Box
                                        component="img"
                                        src={CheckBoxIcon}
                                        alt="Checked"
                                        sx={{ width: 16, height: 16, flexShrink: 0 }}
                                    />
                                    <Typography fontSize={14} lineHeight="24px">
                                        {p}
                                    </Typography>
                                </Stack>
                            ))}
                        </Stack>

                        {/* Why it matters */}
                        <Typography mt={2}>
                            <strong>Why it matters:</strong> {section.why}
                        </Typography>

                        {/* Image */}
                        <Box
                            sx={{
                                mt: 3,
                                width: "100%",
                                height: { xs: "260px", md: "500px" },
                                borderRadius: 3,
                                overflow: "hidden",
                            }}
                        >
                            <img
                                src={section.image}
                                alt={section.title}
                                style={{
                                    width: "100%",
                                    height: { xs: "260px", md: "500px" },
                                    objectFit: "cover",
                                    display: "block",
                                }}
                            />
                        </Box>
                    </Box>
                ))}
            </Box>

            <Box
                className={styles.content}
                sx={{
                    backgroundColor: "#ffffff",
                    py: 6,
                }}
            >
                <Box
                    sx={{
                        maxWidth: 1200,
                        mx: "auto",
                        px: 3,
                    }}
                >
                    <Grid container spacing={4}>
                        {/* Card 1 */}
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    border: "1px solid #0A9A73",
                                    borderRadius: 4,
                                    p: 3,
                                    height: "80%",
                                }}
                            >
                                <Typography fontWeight={700} fontSize={25} mb={1}>
                                    Our Ongoing Commitment
                                </Typography>
                                <Typography fontSize={16} color="text.secondary">
                                    We are committed to continuous improvement in how we deliver care, manage resources, and support our patients.
                                    Sustainability is an ongoing journey, and we regularly evaluate how we can do better—for today and for the future.
                                </Typography>
                            </Box>
                        </Grid>

                        {/* Card 2 */}
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    border: "1px solid #0A9A73",
                                    borderRadius: 4,
                                    p: 3,
                                    height: "80%",
                                }}
                            >
                                <Typography fontWeight={700} fontSize={25} mb={1}>
                                    Our Guiding Principle
                                </Typography>
                                <Typography fontSize={16} color="text.secondary">
                                    Better care.
                                    <br />
                                    Less waste.
                                    <br />
                                    Healthier lives.
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
            <Box sx={{ backgroundColor: "#F2F6F7", py: 5, }}>
                <Box
                    sx={{
                        maxWidth: 850,
                        mx: "auto",
                        px: 3,
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: { xs: 2, md: 10,lg:14 },
                    }}
                >
                    {/* Left content */}
                    <Box>
                        <Typography fontSize={25} fontWeight={700} mb={1}>
                            Have questions about our healthcare <br /> services or sustainability approach?
                        </Typography>
                        <Typography fontSize={16} >
                            Speak with our pharmacy team or contact us to learn more.
                        </Typography>
                    </Box>
                    {/* Button */}
                    <Button
                        variant="contained"
                        size="large"
                        sx={{ width: 220, fontSize: "20px" }}
                    >
                        Click here →
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export default Sustainability;